import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState } from "react";
import ModuleHeader from "./ModuleHeader";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import { uamApi } from "@/api/uamApi";
import { REM_CONFIG } from "@/utils/fontSize";
const AssetDept = ({ className = "" }) => {
    const chartRef = useRef(null);
    const timerRef = useRef(null);
    const [chartData, setChartData] = useState([]);
    const departments = chartData.map((item) => item.deptName) || [];
    const eamCounts = chartData.map((item) => parseInt(item.eamCount) || 0);
    const lcgCounts = chartData.map((item) => parseInt(item.lcgCount) || 0);
    const mmsCounts = chartData.map((item) => parseInt(item.mmsCount) || 0);
    useEffect(() => {
        getData();
    }, []);
    /**
     * @description: getData - 获取数据
     */
    const getData = async () => {
        try {
            const res = await uamApi.totalAssetByDept({ year: "2025" });
            if (res.data) {
                setChartData(res.data);
            }
        }
        catch (error) {
            console.error("获取部门统计数据失败:", error);
        }
    };
    // 自动滚动配置
    const scrollConfig = {
        speed: 0.6,
        visibleCount: 5,
        loop: true,
        interval: 250,
    };
    // ECharts 完整配置（含图例修复）
    const getOption = () => ({
        legend: {
            data: ["固定", "低耗", "物资"],
            right: "center",
            top: 8,
            textStyle: { color: "#fff", fontSize: REM_CONFIG.fontSize.small },
            itemGap: 30, // 图例项间距
            itemWidth: 16, // 标记宽度
            itemHeight: 8, // 标记高度
            icon: "rect",
        },
        dataZoom: [
            {
                type: "inside",
                xAxisIndex: 0,
                start: 0,
                end: departments.length > 0
                    ? Math.min((scrollConfig.visibleCount / departments.length) * 100, 100)
                    : 100,
                show: false,
                zoomLock: true,
                moveOnMouseWheel: false,
            },
        ],
        xAxis: {
            type: "category",
            data: departments,
            axisLabel: {
                color: "#fff",
                fontSize: REM_CONFIG.fontSize.small,
                interval: 0,
                formatter: (value) => {
                    let result = "";
                    for (let i = 0; i < value.length; i++) {
                        if (i !== 0 && i % 4 === 0) {
                            result += "\n";
                        }
                        result += value[i];
                    }
                    return result;
                },
            },
            axisLine: { lineStyle: { color: "#446b9f" } },
            axisTick: { show: false },
        },
        yAxis: {
            type: "value",
            name: "数量（项）",
            nameTextStyle: {
                color: "#fff",
                fontSize: REM_CONFIG.fontSize.small,
            },
            min: 0,
            axisLabel: {
                color: "#fff",
                fontSize: REM_CONFIG.fontSize.small,
                formatter: "{value}",
            },
            axisLine: { lineStyle: { color: "#446b9f" } },
            splitLine: {
                lineStyle: { color: "rgba(68, 107, 159, 0.3)", type: "dashed" },
            },
        },
        series: [
            {
                name: "固定", // 与图例data[0]一致
                type: "bar",
                data: eamCounts,
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#FFD700" },
                        { offset: 1, color: "#DAA520" },
                    ]),
                    borderRadius: [9, 9, 0, 0],
                },
                barWidth: "14%",
                label: {
                    show: true,
                    position: "top",
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                },
            },
            {
                name: "低耗", // 与图例data[1]一致
                type: "bar",
                data: lcgCounts,
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#60FFFF" },
                        { offset: 1, color: "#40C0FF" },
                    ]),
                    borderRadius: [9, 9, 0, 0],
                },
                barWidth: "14%",
                label: {
                    show: true,
                    position: "top",
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                },
            },
            {
                name: "物资", // 与图例data[2]一致
                type: "bar",
                data: mmsCounts,
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#2F96E2" },
                        { offset: 1, color: "#1572C4" },
                    ]),
                    borderRadius: [9, 9, 0, 0],
                },
                barWidth: "14%",
                label: {
                    show: true,
                    position: "top",
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                },
            },
        ],
        grid: {
            left: "4%",
            right: "4%", // 右扩给图例留空间
            bottom: "2%",
            top: 50, // 上扩给换行文字留空间
            containLabel: true,
        },
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
            backgroundColor: "rgba(7, 31, 75, 0.8)",
            borderColor: "#446b9f",
            textStyle: { color: "#fff" },
        },
    });
    // 自动滚动逻辑
    const startAutoScroll = (chart) => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        // 如果没有数据或数据不足以滚动，则不启动轮播
        if (departments.length <= scrollConfig.visibleCount) {
            console.log("数据不足，不启动轮播", departments.length, scrollConfig.visibleCount);
            return;
        }
        let currentStart = 0;
        const totalCount = departments.length;
        const visiblePercent = (scrollConfig.visibleCount / totalCount) * 100;
        const maxStart = 100 - visiblePercent;
        timerRef.current = setInterval(() => {
            currentStart += scrollConfig.speed;
            if (scrollConfig.loop && currentStart >= maxStart) {
                currentStart = 0;
            }
            else if (!scrollConfig.loop && currentStart >= maxStart) {
                currentStart = maxStart;
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
                return;
            }
            chart.dispatchAction({
                type: "dataZoom",
                start: currentStart,
                end: currentStart + visiblePercent,
                dataZoomIndex: 0,
            });
        }, scrollConfig.interval);
    };
    // 初始化与清理
    useEffect(() => {
        // 当数据加载完成后启动轮播
        if (chartData.length > 0) {
            const chartInstance = chartRef.current?.getEchartsInstance();
            if (chartInstance) {
                // 确保图表渲染完成后再启动轮播
                setTimeout(() => {
                    startAutoScroll(chartInstance);
                }, 300);
            }
        }
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
        };
    }, [chartData]); // 依赖chartData数组，数据变化时重新启动轮播
    return (_jsxs("div", { className: `
        relative 
        bg-gradient-to-t 
        from-[rgba(7,31,75,0.5)] 
        to-[rgba(6,32,66,0.5)] 
        
        flex flex-col 
        ${className}
      `, style: { minHeight: 0 }, children: [_jsx(ModuleHeader, { title: "\u4F7F\u7528\u90E8\u95E8\u5206\u5E03" }), _jsx("div", { className: "flex-1", style: { minHeight: 0 }, children: _jsx(ReactEcharts, { ref: chartRef, option: getOption(), style: { width: "100%", height: "100%" }, opts: { renderer: "canvas" }, onChartReady: (chart) => {
                        // 图表准备好后的回调
                        if (chartData.length > 0) {
                            setTimeout(() => {
                                startAutoScroll(chart);
                            }, 300);
                        }
                    } }) })] }));
};
export default AssetDept;
